import { useTranslation } from "react-i18next";
import type { ActionKey } from "../toast/ToastProvider";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import BlockIcon from "@mui/icons-material/Block";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import { resolveLocalized } from "@shared/i18n";
import { guestRequestIsRevenue } from "@shared/guestRequests";
import type {
  GuestRequest,
  GuestRequestStatus,
  GuestRequestTransition,
} from "@shared/types/guestRequest";
import { foyerApi, type TransitionedGuestRequest } from "../../http/foyer";
import { usePermission } from "../../auth/access";
import { tokens } from "../../theme";
import GuestRequestActionDialog from "./GuestRequestActionDialog";
import { describeError, useToast } from "../toast";
import { readLocalized } from "@shared/i18n";
import { useFormat } from "../../i18n/format";
import { translateKey, type Translate } from "../../i18n/translate";
import {
  SECONDARY_TRANSITIONS,
  WAIT_LATE_MS,
  WAIT_WARN_MS,
  availableTransitions,
  formatSlot,
  latestActor,
  latestReason,
  paymentMethodLabel,
  paymentStatusLabel,
  requestAmount,
  requestShape,
  requestStatusLabel,
  selectionLine,
  selectionQuantity,
  transitionLabel,
  waitedLabel,
} from "./guestRequestCopy";

/**
 * The staff inbox — the queue the hotel works its requests off (`POS-9`).
 *
 * ## It polls, and that is POS-6's decision, not an oversight
 *
 * `docs/room-offers.md` §13: *the room is pushed to, the CMS polls*. The
 * television gets `guest_request_changed` over its own room channel because a
 * guest is looking at one screen and needs it to answer; the queue polls for
 * exactly the reason `ServiceRequestQueue` does — a queue that only learns
 * about work through a socket is a queue that silently empties when the socket
 * drops, and an empty queue is indistinguishable from a quiet evening.
 *
 * ## Two clocks, deliberately
 *
 * The list refreshes every {@link POLL_MS}; the "waited" column ages every
 * second. They are separate because they answer different questions: one is
 * "is there new work", the other is "how long has this person been waiting",
 * and tying the second to the first would make a request sitting for twenty
 * minutes look no different from one that landed twenty seconds ago until the
 * next fetch happened to land. A queue whose clock is frozen until you reload
 * is a queue nobody trusts.
 *
 * ## What each row shows, and what it deliberately does not
 *
 * Three shapes, decided by {@link requestShape}:
 *
 * - an **order** carries quantity, amount, how it is paid and whether the
 *   payment has posted;
 * - a **booking** carries the hour it took and the party it seats;
 * - an **asap** booking — the "call for staff" flow — carries the room, the
 *   clock and whatever was picked, and nothing else, *because nothing else
 *   exists*. Drawing an empty slot cell for it would read as data that failed
 *   to load rather than as a request that never had any.
 *
 * A **zero-price request is legitimate** and is never drawn as a problem: the
 * amenity picker (towels, refills) is the third demo flow, and §7 is explicit
 * that an absent price means free rather than zero.
 *
 * ## The buttons come from the machine
 *
 * {@link availableTransitions} asks `@shared/guestRequests` which verbs the row
 * admits. Nothing here knows that `declined` is final or that `refund` is
 * reachable from two states — so a final row simply offers nothing, and a sixth
 * edge added to §13 would appear here without this file changing.
 */

/** How often the list refetches. Care's queue uses the same interval. */
const POLL_MS = 8000;
/** How often the waiting clock moves. One second, because that is what "ages
 *  visibly" means to somebody watching a demo. */
const TICK_MS = 1000;

/** Joins the facts of one row on one line. */
const SEPARATOR = " · ";

/** The statuses that are still WORK. Everything else is history. */
const OPEN_STATUSES: readonly GuestRequestStatus[] = ["pending", "accepted"];

const STATUS_COLOUR: Record<GuestRequestStatus, string> = {
  pending: tokens.warning,
  accepted: tokens.brand,
  completed: tokens.success,
  declined: tokens.inkFaint,
  cancelled: tokens.inkFaint,
  refunded: tokens.error,
};

/** The three the ticket names, in the order a shift works them. */
const PRIMARY_TRANSITIONS: readonly GuestRequestTransition[] = [
  "accept",
  "complete",
  "decline",
];

const TRANSITION_ICON: Partial<
  Record<GuestRequestTransition, React.ReactNode>
> = {
  accept: <CheckCircleIcon fontSize="small" />,
  complete: <DoneAllIcon fontSize="small" />,
  decline: <BlockIcon fontSize="small" />,
};

const TRANSITION_FAILED: Record<GuestRequestTransition, ActionKey> = {
  accept: "acceptGuestRequest",
  complete: "completeGuestRequest",
  decline: "declineGuestRequest",
  cancel: "cancelGuestRequest",
  refund: "refundGuestRequest",
};

export interface GuestRequestQueueViewProps {
  requests: GuestRequest[];
  /** The clock, passed in so the caller owns the ticking (and tests own it too). */
  now: number;
  defaultLocale: string;
  /**
   * How a room is NAMED — its number, and nothing else.
   *
   * Deliberately not "Standard (303)": this string is also the one the accept
   * dialog puts in *"This books 450 kr to room …"*, and a sentence that reads
   * "to room Standard (303)" is a sentence written by a database. The room
   * TYPE goes underneath as a caption, where it is a detail rather than the
   * identity.
   */
  roomLabel: (roomId: string) => string;
  /** The room's own name, when it has one worth showing under the number. */
  roomName?: (roomId: string) => string | null;
  /** False hides every control that would be refused by the server anyway. */
  canHandle: boolean;
  busyId?: string | null;
  loading?: boolean;
  error?: string | null;
  showSettled: boolean;
  onShowSettled: (value: boolean) => void;
  onRefresh: () => void;
  onAct: (request: GuestRequest, transition: GuestRequestTransition) => void;
  /** Names the hotel, when several queues are stacked on a chain page. */
  title?: string;
}

/** The list itself, with no fetching in it, so its rendering is testable. */
export function GuestRequestQueueView({
  requests,
  now,
  defaultLocale,
  roomLabel,
  roomName,
  canHandle,
  busyId,
  loading,
  error,
  showSettled,
  onShowSettled,
  onRefresh,
  onAct,
  title,
}: GuestRequestQueueViewProps) {
  const { t } = useTranslation(["offers", "common"]);
  const waiting = requests.filter((r) => r.status === "pending").length;
  const visible = requests.filter(
    (r) => showSettled || OPEN_STATUSES.includes(r.status)
  );

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.25}
        flexWrap="wrap"
        data-testid="request-queue-toolbar"
      >
        {title && (
          <Typography sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}>
            {title}
          </Typography>
        )}
        <Chip
          size="small"
          label={
            waiting === 0
              ? t("guestRequestQueue.nothingWaiting")
              : t("guestRequestQueue.waiting", { count: waiting })
          }
          data-testid="request-waiting-count"
          sx={{
            height: 22,
            typography: "micro",
            fontWeight: "fontWeightBold",
            color: waiting ? tokens.warning : tokens.inkMuted,
            borderColor: waiting ? tokens.warning : tokens.divider,
          }}
          variant="outlined"
        />
        {loading && <CircularProgress size={14} />}
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showSettled}
              onChange={(e) => onShowSettled(e.target.checked)}
              data-testid="request-show-settled"
            />
          }
          label={
            <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
              {t("guestRequestQueue.showSettled")}
            </Typography>
          }
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          data-testid="request-refresh"
        >
          {t("common:buttons.refresh")}
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Box
        sx={{
          border: `1px solid ${tokens.divider}`,
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: tokens.surface,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("guestRequestQueue.columns.unit")}</TableCell>
              <TableCell>{t("guestRequestQueue.columns.request")}</TableCell>
              <TableCell>{t("guestRequestQueue.columns.whatItIs")}</TableCell>
              <TableCell>{t("guestRequestQueue.columns.waited")}</TableCell>
              <TableCell>{t("guestRequestQueue.columns.status")}</TableCell>
              <TableCell align="right" width={canHandle ? 300 : 0} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((request) => (
              <QueueRow
                key={request.id}
                request={request}
                now={now}
                defaultLocale={defaultLocale}
                roomLabel={roomLabel}
                roomName={roomName}
                canHandle={canHandle}
                busy={busyId === request.id}
                onAct={onAct}
              />
            ))}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Stack alignItems="center" sx={{ py: 4 }} gap={0.5}>
                    <Typography
                      sx={{
                        fontWeight: "fontWeightSemibold",
                        color: tokens.ink,
                      }}
                    >
                      {loading
                        ? t("common:states.loading")
                        : t("guestRequestQueue.nothingWaiting")}
                    </Typography>
                    {!loading && (
                      <Typography
                        variant="bodySm"
                        sx={{
                          color: tokens.inkMuted,
                          textAlign: "center",
                          maxWidth: 460,
                        }}
                      >
                        {t("guestRequestQueue.emptyHelp")}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */

function QueueRow({
  request,
  now,
  defaultLocale,
  roomLabel,
  roomName,
  canHandle,
  busy,
  onAct,
}: {
  request: GuestRequest;
  now: number;
  defaultLocale: string;
  roomLabel: (roomId: string) => string;
  roomName?: (roomId: string) => string | null;
  canHandle: boolean;
  busy: boolean;
  onAct: (request: GuestRequest, transition: GuestRequestTransition) => void;
}) {
  const { t } = useTranslation("offers");
  const fmt = useFormat();
  const shape = requestShape(request);
  const amount = requestAmount(request);
  const picks = selectionLine(t, request, defaultLocale);
  const reason = latestReason(request);
  const actor = latestActor(request);
  const allowed = availableTransitions(request.status);
  const primary = PRIMARY_TRANSITIONS.filter((v) => allowed.includes(v));
  const secondary = SECONDARY_TRANSITIONS.filter((v) => allowed.includes(v));
  const waitedMs = Math.max(0, now - new Date(request.createdAt).getTime());
  // Only a request nobody has touched is "late". Once staff have accepted it
  // the clock is a fact, not an alarm — the hotel has taken it on.
  const urgent = request.status === "pending" && waitedMs >= WAIT_LATE_MS;
  const nagging = request.status === "pending" && waitedMs >= WAIT_WARN_MS;

  return (
    <TableRow hover data-testid={`request-row-${request.id}`}>
      <TableCell>
        <Typography
          variant="body2"
          sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}
        >
          {roomLabel(request.roomId)}
        </Typography>
        {roomName?.(request.roomId) && (
          <Typography variant="micro" sx={{ color: tokens.inkFaint }}>
            {roomName(request.roomId)}
          </Typography>
        )}
      </TableCell>

      <TableCell>
        {/* No glyph: a `GuestRequest` snapshots the offer's TITLE and the item
            labels, never an icon key, and looking one up in today's catalogue
            would draw last week's order with this week's picture. */}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: tokens.ink }}>
            {resolveLocalized(
              request.offerTitle,
              defaultLocale,
              defaultLocale
            ) || t("guestRequestQueue.untitledOffer")}
          </Typography>
          <Typography variant="micro" sx={{ color: tokens.inkFaint }}>
            {t(`guestRequestQueue.shape.${shape}`)}
          </Typography>
        </Box>
      </TableCell>

      <TableCell sx={{ color: tokens.inkMuted }}>
        <Stack spacing={0.25}>
          {/* An order: what and how many, then the money. */}
          {shape === "order" && (
            <>
              {picks && (
                <Typography variant="bodySm" sx={{ color: tokens.ink }}>
                  {picks}
                </Typography>
              )}
              <Typography variant="meta">
                {[
                  t("guestRequestQueue.itemCount", {
                    count: selectionQuantity(request),
                  }),
                  amount,
                  paymentMethodLabel(t, request.payment.method),
                  request.payment.method === "none"
                    ? null
                    : paymentStatusLabel(t, request.payment.status),
                ]
                  .filter(Boolean)
                  .join(SEPARATOR)}
              </Typography>
            </>
          )}

          {/* A booking: the hour it took, and who it seats. */}
          {shape === "booking" && (
            <>
              <Typography variant="bodySm" sx={{ color: tokens.ink }}>
                {formatSlot(t, fmt, request.slotStart ?? "")}
              </Typography>
              <Typography variant="meta">
                {[
                  request.partySize
                    ? t("guestRequestQueue.partyOf", {
                        count: request.partySize,
                      })
                    : t("guestRequestQueue.partyNotStated"),
                  amount,
                  request.payment.method === "none"
                    ? null
                    : paymentMethodLabel(t, request.payment.method),
                ]
                  .filter(Boolean)
                  .join(SEPARATOR)}
              </Typography>
            </>
          )}

          {/* Asked for staff: the room and the clock are the whole story. The
              picked amenity is the one thing worth adding, because it is what
              somebody has to carry up. No price, because there is none. */}
          {shape === "asap" && (
            <Typography variant="bodySm" sx={{ color: tokens.ink }}>
              {picks || t("guestRequestQueue.nothingPicked")}
            </Typography>
          )}

          {request.note && (
            <Typography variant="meta" sx={{ fontStyle: "italic" }}>
              {t("guestRequestQueue.note", { note: request.note })}
            </Typography>
          )}
          {reason && (
            <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
              {t("guestRequestQueue.reason", { reason })}
            </Typography>
          )}
        </Stack>
      </TableCell>

      <TableCell>
        <Typography
          variant="bodySm"
          data-testid={`request-waited-${request.id}`}
          sx={{
            fontWeight: urgent ? "fontWeightBold" : "fontWeightMedium",
            color: urgent
              ? tokens.error
              : nagging
                ? tokens.warning
                : tokens.inkMuted,
          }}
        >
          {waitedLabel(t, request.createdAt, now)}
        </Typography>
      </TableCell>

      <TableCell>
        <Stack spacing={0.25} alignItems="flex-start">
          <Chip
            size="small"
            variant="outlined"
            label={requestStatusLabel(t, request.status)}
            data-testid={`request-status-${request.id}`}
            sx={{
              height: 22,
              typography: "micro",
              fontWeight: "fontWeightBold",
              color: STATUS_COLOUR[request.status],
              borderColor: STATUS_COLOUR[request.status],
            }}
          />
          {/* Every move stamps an actor (§13). Showing it is what makes the
              stamp worth taking — an accepted row whose acceptance nobody owns
              is exactly what the rule exists to prevent. */}
          {actor && request.status !== "pending" && (
            <Typography variant="micro" sx={{ color: tokens.inkFaint }}>
              {t("guestRequestQueue.by", { actor })}
            </Typography>
          )}
          {guestRequestIsRevenue(request.status) && amount && (
            <Typography variant="micro" sx={{ color: tokens.success }}>
              {t("guestRequestQueue.booked", { amount })}
            </Typography>
          )}
        </Stack>
      </TableCell>

      <TableCell align="right">
        {canHandle && (
          <Stack direction="row" gap={1} justifyContent="flex-end">
            {primary.map((verb) => (
              <Button
                key={verb}
                size="small"
                variant={verb === "accept" ? "contained" : "outlined"}
                color={verb === "decline" ? "inherit" : "primary"}
                startIcon={TRANSITION_ICON[verb]}
                disabled={busy}
                onClick={() => onAct(request, verb)}
                data-testid={`request-${verb}-${request.id}`}
              >
                {transitionLabel(t, verb)}
              </Button>
            ))}
            {secondary.length > 0 && (
              <RowMenu
                requestId={request.id}
                transitions={secondary}
                disabled={busy}
                onPick={(verb) => onAct(request, verb)}
              />
            )}
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
}

/** Cancel and refund, out of the way of the three verbs a shift actually uses. */
function RowMenu({
  requestId,
  transitions,
  disabled,
  onPick,
}: {
  requestId: string;
  transitions: readonly GuestRequestTransition[];
  disabled?: boolean;
  onPick: (verb: GuestRequestTransition) => void;
}) {
  const { t } = useTranslation("offers");
  const [el, setEl] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        disabled={disabled}
        onClick={(e) => setEl(e.currentTarget)}
        aria-label={t("guestRequestQueue.moreActions")}
        data-testid={`request-more-${requestId}`}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={el} open={Boolean(el)} onClose={() => setEl(null)}>
        {transitions.map((verb) => (
          <MenuItem
            key={verb}
            onClick={() => {
              setEl(null);
              onPick(verb);
            }}
            data-testid={`request-${verb}-${requestId}`}
          >
            {transitionLabel(t, verb)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/* -------------------------------------------------------------------------- */

export interface GuestRequestQueueProps {
  premiseId: string;
  /** For the room column. A queue that says `65f0…c1` helps nobody. */
  rooms?: { id: string; name?: string }[];
  defaultLocale?: string;
  /** Names the hotel when several queues share a page (the chain view). */
  title?: string;
}

/**
 * One premise's queue, wired: polls, ages, and moves rows through the machine.
 *
 * Mounted once per hotel — the chain page stacks several of these rather than
 * asking for a company-layer list, because a request belongs to a room and a
 * room belongs to one location. There is no such thing as a company's request.
 */
export default function GuestRequestQueue({
  premiseId,
  rooms,
  defaultLocale = "sv",
  title,
}: GuestRequestQueueProps) {
  const toast = useToast();
  const { t } = useTranslation(["offers", "common"]);
  const canHandle = usePermission("guest-requests", "edit", { premiseId });
  const [requests, setRequests] = useState<GuestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSettled, setShowSettled] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [pending, setPending] = useState<{
    request: GuestRequest;
    transition: GuestRequestTransition;
  } | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  // A poll landing after the component went away must not set state.
  const alive = useRef(true);
  /**
   * When the last write landed, and whether one is in flight.
   *
   * A poll issued a moment BEFORE Accept resolves would otherwise land after it
   * and replace the list with the pre-transition rows — the row flips back to
   * "New" with a live Accept button, and the second press is a 409 the operator
   * cannot explain. So a fetch that started before the newest write is thrown
   * away rather than applied, and no poll is applied at all while a write is in
   * flight. The next tick, eight seconds later, is authoritative again.
   */
  const lastWriteAt = useRef(0);
  const writesInFlight = useRef(0);

  // The number IS the room, and this string also lands in "…to room 412".
  const roomLabel = useCallback((id: string) => id, []);
  // The type ("Standard", "Svit") as a caption. Useful to a porter, and never
  // the thing the room is called.
  const roomName = useCallback(
    (id: string) => rooms?.find((r) => r.id === id)?.name ?? null,
    [rooms]
  );

  const refresh = useCallback(async () => {
    const startedAt = Date.now();
    try {
      const list = await foyerApi.listGuestRequests(premiseId);
      if (!alive.current) return;
      // Stale by the time it arrived: a transition has landed since this fetch
      // was issued, and its answer is the newer truth.
      if (writesInFlight.current > 0 || lastWriteAt.current > startedAt) return;
      setRequests(list);
      setError(null);
    } catch (err) {
      if (!alive.current) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [premiseId]);

  useEffect(() => {
    alive.current = true;
    void refresh();
    const poll = setInterval(() => void refresh(), POLL_MS);
    // The second clock. Separate from the poll on purpose — see the docblock.
    const tick = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => {
      alive.current = false;
      clearInterval(poll);
      clearInterval(tick);
    };
  }, [refresh]);

  const commit = async (
    transition: GuestRequestTransition,
    reason?: string
  ) => {
    if (!pending) return;
    const id = pending.request.id;
    setBusyId(id);
    setDialogError(null);
    writesInFlight.current += 1;
    let refused = false;
    try {
      const saved = await foyerApi.transitionGuestRequest(
        premiseId,
        id,
        transition,
        reason
      );
      // Adopt the server's own row rather than patching the status locally: it
      // is the row that carries the new stamp, and a locally-guessed status
      // would disagree with the audit line printed underneath it.
      lastWriteAt.current = Date.now();
      setRequests((prev) => prev.map((r) => (r.id === saved.id ? saved : r)));
      setPending(null);
      const what =
        readLocalized(pending.request.offerTitle, defaultLocale) ||
        t("guestRequestQueue.requestFallback");
      toast.success(
        t(`guestRequestQueue.done.${transition}`, {
          what,
          room: pending.request.roomId,
        })
      );
      const folioWarning = describeFolioOutcome(
        t,
        transition,
        pending.request.roomId,
        saved.folio
      );
      // Kept until dismissed: it is a job for somebody, not a status line.
      if (folioWarning) toast.error(folioWarning, { durationMs: 0 });
    } catch (err) {
      toast.error(
        describeError(t, TRANSITION_FAILED[transition], err, {
          room: pending.request.roomId,
        })
      );
      // A lost race ("somebody else already accepted this") answers 409. Say so
      // and refetch, rather than leaving a dialog that seems to have worked.
      setDialogError(err instanceof Error ? err.message : String(err));
      refused = true;
    } finally {
      writesInFlight.current -= 1;
      setBusyId(null);
    }
    // Outside the `finally`, so the counter is already back to zero and the
    // guard in `refresh` does not throw away the very answer we are asking for.
    if (refused) void refresh();
  };

  return (
    <>
      <GuestRequestQueueView
        requests={requests}
        now={now}
        defaultLocale={defaultLocale}
        roomLabel={roomLabel}
        roomName={roomName}
        canHandle={canHandle}
        busyId={busyId}
        loading={loading}
        error={error}
        showSettled={showSettled}
        onShowSettled={setShowSettled}
        onRefresh={() => void refresh()}
        onAct={(request, transition) => {
          setDialogError(null);
          setPending({ request, transition });
        }}
        title={title}
      />
      {pending && (
        // Keyed by row AND verb: the remount is the reset, so a reason typed
        // for one decline can never follow the operator to the next row.
        <GuestRequestActionDialog
          key={`${pending.request.id}:${pending.transition}`}
          request={pending.request}
          transition={pending.transition}
          roomLabel={roomLabel(pending.request.roomId)}
          busy={busyId === pending.request.id}
          error={dialogError}
          onClose={() => setPending(null)}
          onConfirm={(reason) => void commit(pending.transition, reason)}
        />
      )}
    </>
  );
}

/**
 * What staff must do when the folio did not follow the transition (`MEWS-5`),
 * or null when it did. The server sends codes; the sentence lives here.
 *
 * Exported for the tests. Two cases matter most: an accepted order that did
 * NOT reach the guest's bill (somebody has to charge it by hand), and a refund
 * the PMS cannot reverse (somebody has to void it there).
 */
export function describeFolioOutcome(
  t: Translate,
  transition: GuestRequestTransition,
  roomId: string,
  folio: TransitionedGuestRequest["folio"]
): string | null {
  if (!folio || folio.charged) return null;
  const pms = folio.vendor ?? t("offers:guestRequestQueue.folio.thePms");
  const params = { room: roomId, pms };
  if (transition === "refund") {
    return folio.code === "reverse_unsupported"
      ? t("offers:guestRequestQueue.folio.refundedVoidByHand", params)
      : t("offers:guestRequestQueue.folio.refundedNotReversed", params);
  }
  // A timeout is NOT "not charged": the order may exist. Charging by hand on
  // top of it would bill the guest twice, so staff are sent to look first.
  if (folio.code === "unreachable" || folio.code === "posting_in_flight") {
    return t("offers:guestRequestQueue.folio.acceptedUnconfirmed", params);
  }
  const KNOWN = [
    "no_stay",
    "unmapped",
    "ambiguous_room",
    "not_chargeable",
    "currency_mismatch",
    "credential_unavailable",
  ];
  const code = folio.code ?? "";
  const reason = translateKey(
    t,
    `offers:guestRequestQueue.folio.why.${KNOWN.includes(code) ? code : "refused"}`,
    params
  );
  return t("offers:guestRequestQueue.folio.notCharged", { reason });
}
